import { useState, useContext, useCallback, FC, useEffect } from 'react'
import {
  AptiveIcon,
  Button,
  Toast,
} from '@aptive-env/storybook'
import { format, startOfToday } from 'date-fns'
import ParticipantExpandableList from 'components/ParticipantExpandableList'
import { EventDataWarningModal } from 'components/EventDataWarningModal'
import { CalendarEventTypeSelect } from 'components/CalendarEventTypeSelect'
import {
  EventTimePicker,
  EventLocationInput,
  EventDescriptionInput,
  EventTitleInput,
  EventGuestSelect,
  EventRepeatSelect,
} from './EventModalComponents'
import {
  updateCalendarEvent,
  createCalendarEvent,
  overrideCalendarEvent,
  addCalendarEventParticipants,
} from 'services/calendar'
import { IAddress } from 'interface/address'
import { ICoordinates } from 'interface/map'
import {
  ICalendarEventParticipant,
  ICalendarEventType,
  IEventData,
  IErrors,
} from 'interface/calendar'
import { CalendarStateContext } from 'context/CalendarContext'
import { convert24HourTo12Hour, convert12HourTo24Hour } from 'utils/convertHour'
import { isPastTime } from 'utils/calendar'
import {
  eventLabelConstants,
  eventValidationConstants,
  eventMessagesConstants,
  eventConstants,
} from 'constants/index'

interface EventUpdateModalProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  updateType: string
  event?: any
  onUpdate?: () => void
  eventParticipants: ICalendarEventParticipant[]
  eventDate?: Date
  onSuccess: (success: string) => void
  onClose?: () => void
}

const {
  EVENT_CREATED_SUCCESS,
  EVENT_EDITED_SUCCESS,
  EVENT_OVERRIDE_SUCCESS,
} = eventMessagesConstants

const {
  EDIT_EVENT_LABEL,
  NEW_EVENT_LABEL,
  SAVE_BUTTON_LABEL,
  ADD_EVENT_BUTTON_LABEL,
  CANCEL_BUTTON_LABEL,
} = eventLabelConstants

const {
  EVENT_TITLE_MAX_LENGTH,
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_TITLE_MISSING_WARNING_CONTENT,
  EVENT_TITLE_MISSING_WARNING_TITLE,
  EVENT_PAST_DATE_WARNING_TITLE,
  EVENT_PAST_DATE_WARNING_CONTENT,
} = eventValidationConstants

const {
  EVENT_EDIT_TYPE,
  EVENT_NEW_TYPE,
  EVENT_OVERRIDE_TYPE,
  EVENT_INITIAL_TITLE,
  EVENT_INITIAL_START_TIME,
  EVENT_INITIAL_END_TIME,
} = eventConstants

const emptyLocationData: IAddress = {
  address: '',
  city: '',
  state: '',
  zip: '',
}

const emptyGeoLocation: ICoordinates = {
  lat: 0,
  lng: 0,
}

export const EventUpdateModal: FC<EventUpdateModalProps> = ({
  isOpen,
  setIsOpen,
  updateType,
  event,
  onUpdate,
  eventParticipants,
  eventDate,
  onSuccess,
  onClose,
}) => {
  const { eventTypes, selectedOffice } = useContext(CalendarStateContext)

  const [eventData, setEventData] = useState<IEventData>({
    title: '',
    description: '',
    selectedDate: startOfToday(),
    selectedStartTime: EVENT_INITIAL_START_TIME,
    selectedEndTime: EVENT_INITIAL_END_TIME,
    eventType: eventTypes[0],
    participants: [],
    locationData: emptyLocationData,
    geoLocation: emptyGeoLocation,
    interval: 'once',
    endAfter: 'never',
  })
  const [updateEvent, setUpdateEvent] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<IErrors>({})
  const [isEventDataWarning, setIsEventDataWarning] = useState(false)
  const [eventWarningData, setEventWarningData] = useState({
    title: '',
    content: '',
  })

  const initAddress = event
    ? event.address?.address
      ? event.address.address
      : [event.address?.city, event.address?.state, event.address?.zip]
        .filter(Boolean)
        .join(', ')
    : ''

  const parseEventType = useCallback(
    (eventType: string) => {
      return eventTypes.find((type) => type.id === eventType) ?? eventTypes[0]
    },
    [eventTypes]
  )

  useEffect(() => {
    if (event) {
      setEventData((prevData) => ({
        ...prevData,
        title: event.title,
        description: event.description,
        selectedStartTime: convert24HourTo12Hour(event.start_at),
        selectedEndTime: convert24HourTo12Hour(event.end_at),
        locationData: event.address || emptyLocationData,
        geoLocation: event.location || emptyGeoLocation,
        eventType: parseEventType(event.event_type),
        interval: event.interval,
        repeatEvery: event.repeat_every,
        weekDays: event.week_days,
        weekNum: event.week_num,
        occurrences: event.occurrences,           
      }))
    }
    if (eventDate) {
      setEventData((prevData) => ({ ...prevData, selectedDate: eventDate }))
    }
    if (eventParticipants) {
      setEventData((prevData) => ({
        ...prevData,
        participants: eventParticipants,
      }))
    }
  }, [setEventData, event, eventDate, eventParticipants, eventTypes, parseEventType])

  const handleUpdate = async () => {
    setIsSaving(true)
    const updateData = {
      title: eventData.title,
      description: eventData.description,
      location_lat: eventData.geoLocation.lat,
      location_lng: eventData.geoLocation.lng,
      address: eventData.locationData.address,
      city: eventData.locationData.city,
      state: eventData.locationData.state,
      zip: eventData.locationData.zip,
    }
    let params = {} as any
    try {
      if (updateType === EVENT_NEW_TYPE) {
        params = {
          ...updateData,
          office_id: selectedOffice.id,
          event_type: eventData.eventType?.id,
          start_date: format(eventData.selectedDate, 'yyyy-MM-dd'),
          ...(eventData.endDate && { end_date: eventData.endDate }),
          start_at: convert12HourTo24Hour(eventData.selectedStartTime),
          end_at: convert12HourTo24Hour(eventData.selectedEndTime),
          interval: eventData.interval,
          end_after: eventData.endAfter,
          ...(eventData.weekDays && { week_days: eventData.weekDays }),
          ...(eventData.weekNum && { week_num: eventData.weekNum }),
          ...(eventData.occurrences && { occurrences: eventData.occurrences }),
          ...(eventData.repeatEvery && { repeat_every: eventData.repeatEvery }),
        }
      }
      if (updateType === EVENT_EDIT_TYPE) {
        params = {
          ...updateData,
          eventId: event.id,
        }
        if (onUpdate) {
          onUpdate()       
        }
      }
      if (updateType === EVENT_OVERRIDE_TYPE) {
        params = {
          ...updateData,
          eventId: event.id,
          date: eventDate ? format(eventDate, 'yyyy-MM-dd') : event.date,
          start_time: convert12HourTo24Hour(eventData.selectedStartTime),
          end_time: convert12HourTo24Hour(eventData.selectedEndTime),
          is_canceled: false,
          meeting_link: event.meeting_link,
        }
        if (onUpdate) {
          onUpdate()
        }
      }

      const eventAction = updateType === EVENT_NEW_TYPE
        ? createCalendarEvent
        : updateType === EVENT_EDIT_TYPE
          ? updateCalendarEvent
          : overrideCalendarEvent
      const response = await eventAction(params)

      if (response._metadata.success) {
        const eventId = updateType === EVENT_OVERRIDE_TYPE ? event.id : response.result.id
        const eventParticipants = eventData.participants?.map((guest) => guest.id)

        if (eventId && eventParticipants.length > 0) {
          const addParticipantsResponse = await addCalendarEventParticipants(eventId, eventParticipants)
          if (addParticipantsResponse._metadata.success) {
            onSuccess(updateType === EVENT_NEW_TYPE ? EVENT_CREATED_SUCCESS : updateType === EVENT_EDIT_TYPE ? EVENT_EDITED_SUCCESS : EVENT_OVERRIDE_SUCCESS)
          } else if (response.result.errors) {
            setErrors(response.result.errors)
          }
        } else {
          onSuccess(updateType === EVENT_NEW_TYPE ? EVENT_CREATED_SUCCESS : updateType === EVENT_EDIT_TYPE ? EVENT_EDITED_SUCCESS : EVENT_OVERRIDE_SUCCESS)
        }
      } else if (response.result.errors) {
        setErrors(response.result.errors)
      }
    } catch (e) {
      console.log(e)
    }
    setIsSaving(false)
    setUpdateEvent(false)
  }

  useEffect(() => {
    if (updateEvent)
      handleUpdate()
    // eslint-disable-next-line
  }, [updateEvent])

  const isDataInvalid = () => {
    const trimmedTitle = eventData.title.trim()
    const trimmedDescription = eventData.description.trim()
    setEventData((prevData) => ({
      ...prevData,
      title: trimmedTitle,
      description: trimmedDescription,
    }))

    const titleErrors: string[] = []
    const descriptionErrors: string[] = []
    if (trimmedTitle && trimmedTitle.length > 100) {
      titleErrors.push(EVENT_TITLE_MAX_LENGTH)
    }

    if (trimmedDescription && trimmedDescription.length > 500) {
      descriptionErrors.push(EVENT_DESCRIPTION_MAX_LENGTH)
    }

    setErrors({
      title: titleErrors,
      description: descriptionErrors,
    })

    return titleErrors.length || descriptionErrors.length
  }

  const handleClose = () => {
    if (onClose) onClose()
    setIsOpen(false)
  }

  const handleOnClickSave = (warning?: string) => {
    if (isDataInvalid()) return
    let currentTitle = eventData.title
    let allowPastDate = false

    if (warning === EVENT_TITLE_MISSING_WARNING_TITLE) {
      setEventData((prevData) => ({ ...prevData, title: EVENT_INITIAL_TITLE }))
      currentTitle = EVENT_INITIAL_TITLE
    }

    if (warning === EVENT_PAST_DATE_WARNING_TITLE) allowPastDate = true

    setIsEventDataWarning(false)
    setEventWarningData({ title: '', content: '' })

    if (currentTitle.length === 0) {
      setEventWarningData({
        title: EVENT_TITLE_MISSING_WARNING_TITLE,
        content: EVENT_TITLE_MISSING_WARNING_CONTENT,
      })
      setIsEventDataWarning(true)
    } else if (
      isPastTime(
        eventData.selectedDate,
        eventData.selectedStartTime,
        selectedOffice.timezone?.timezone || ''
      ) &&
          !allowPastDate
    ) {
      setEventWarningData({
        title: EVENT_PAST_DATE_WARNING_TITLE,
        content: EVENT_PAST_DATE_WARNING_CONTENT,
      })
      setIsEventDataWarning(true)
    } else {
      setUpdateEvent(true)
    }
  }

  const handleSelectEventType = (selected: ICalendarEventType) => {
    setEventData((prevData) => ({ ...prevData, eventType: selected }))
  }

  return (
    <>
      {isOpen && (
        <div className="sidebar fixed top-0 left-0 h-full w-full z-[50]">
          <div className="bg-[#0000001A] absolute top-0 left-0 h-full w-full" onClick={handleClose}></div>
          <div className="flex flex-col justify-between absolute right-0 bg-white h-full w-[384px] p-4 border overflow-x-hidden transition-all duration-500">
            <div className="flex flex-col">
              <div className="flex justify-between items-center h-5">
                <div className="font-inter text-[16px] font-semibold leading-5 text-dark-gray-100">
                  {event ? EDIT_EVENT_LABEL : NEW_EVENT_LABEL}
                </div>
                <AptiveIcon
                  icon="x"
                  className="w-[20px] h-[20px] stroke-[#3D3D3D] fill-none cursor-pointer"
                  onClick={handleClose}
                />
              </div>

              <div className="mt-5 flex flex-col gap-6">
                <EventTitleInput
                  eventData={eventData}
                  setEventData={setEventData}
                  errors={errors}
                  setErrors={setErrors}
                />
                <CalendarEventTypeSelect
                  items={eventTypes}
                  selected={eventData.eventType}
                  onSelect={handleSelectEventType}
                  disabled={updateType === EVENT_EDIT_TYPE || updateType === EVENT_OVERRIDE_TYPE}
                />
                <EventTimePicker
                  eventData={eventData}
                  setEventData={setEventData}
                  disabled={updateType === EVENT_EDIT_TYPE}
                />
                <EventRepeatSelect
                  eventData={eventData}
                  setEventData={setEventData}
                  isOpen={isOpen}
                  disabled={updateType === EVENT_EDIT_TYPE || updateType === EVENT_OVERRIDE_TYPE}
                  event={event}
                />
                <EventGuestSelect
                  eventData={eventData}
                  setEventData={setEventData}
                />
                {eventData.participants.length > 0 && (
                  <ParticipantExpandableList
                    participants={eventData.participants}
                  />
                )}
                <EventLocationInput
                  initAddress={initAddress}
                  setEventData={setEventData}
                  setErrors={setErrors}
                />
                <EventDescriptionInput
                  eventData={eventData}
                  setEventData={setEventData}
                  errors={errors}
                  setErrors={setErrors}
                />
              </div>
            </div>

            <div className="bottom-5 mt-10">
              <div className="flex justify-end">
                <Button
                  variant="neutral"
                  onClick={handleClose}
                  className="w-[168px] h-9 text-[#4B5563]"
                >
                  {CANCEL_BUTTON_LABEL}
                </Button>
                <Button
                  className="text-ui-snow bg-[#007AFF] ml-[10px] w-[168px] h-9 text-14 font-medium"
                  variant="custom"
                  disabled={isSaving}
                  onClick={() => handleOnClickSave()}
                >
                  {event ? SAVE_BUTTON_LABEL : ADD_EVENT_BUTTON_LABEL}
                </Button>
              </div>
            </div>
          </div>

          <EventDataWarningModal
            open={isEventDataWarning}
            data={eventWarningData}
            onClickGoBack={() => setIsEventDataWarning(false)}
            onClickSave={handleOnClickSave}
          />

          {errors.address && (
            <div className="fixed top-10 right-10">
              <Toast
                dismissible
                isIcon
                onDismiss={() => setErrors((prevErrors) => ({ ...prevErrors, address: '' }))}
                status="warning"
              >
                {errors.address}
              </Toast>
            </div>
          )}
        </div>
      )}
    </>
  )
}
